// @flow
import moment from 'moment';
import { isValidDate } from './validation';
// eslint-disable-next-line max-len
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import { NO_PREFERENCE_KEY } from '../../../utils/noPreference';

// ConvertProspectToApplicantPayloadSchema is a plain Joi object, so one key it
// does not name rejects the whole request. Every value is placed by name and
// nothing is spread.
//
// The schema allows null on most of these, but the form has no way to say
// "cleared" — an unanswered field, and a field the property configuration hid
// and react-hook-form unregistered, are both simply absent.
const isBlank = (value: mixed) =>
  value === undefined || value === null || value === '';

const present = (key: string, value: mixed) =>
  isBlank(value) ? {} : { [key]: value };

// The schema takes a bedroom entry as an integer or as the one No Preference
// string; the multi-select stores both as strings.
const toBedroom = (bedroom: string) =>
  bedroom === NO_PREFERENCE_KEY ? bedroom : Number(bedroom);

const toMailingAddress = (values: Object) => {
  const mailingAddress = {
    ...present('street', values.address),
    ...present('city', values.city),
    ...present('stateId', values.state),
    ...present('zipCode', values.zipCode),
  };

  return Object.keys(mailingAddress).length ? { mailingAddress } : {};
};

const toPrimaryApplicant = (values: Object) => {
  const primaryApplicant = {
    ...present('firstName', values.firstName),
    ...present('middleName', values.middleName),
    ...present('lastName', values.lastName),
    ...present('suffixId', values.suffix),
    ...present('phoneNumber', values.phoneNumber),
    ...present('phoneType', values.phoneType),
    ...present('emailAddress', values.emailAddress),
    ...present('emailType', values.emailType),
    ...present(
      'dateOfBirth',
      values.dateOfBirth ? formatDateDB(values.dateOfBirth) : undefined,
    ),
    ...present('gender', values.gender),
    // An untouched multi-select holds an empty array rather than a blank, and
    // the schema reads that as "reported no races" rather than as unanswered.
    ...present(
      'multiRace',
      values.multiRace?.length ? values.multiRace : undefined,
    ),
    ...present('ethnicity', values.ethnicity),
    ...toMailingAddress(values),
  };

  return Object.keys(primaryApplicant).length ? { primaryApplicant } : {};
};

export const toConvertPayload = (values: Object): Object => ({
  adults: Number(values.adults),
  minors: Number(values.minors),
  ...toPrimaryApplicant(values),
  ...present('floorPlanId', values.floorPlan),
  ...present('bedrooms', values.bedrooms?.map(toBedroom)),
  ...present('selectedPrograms', values.selectedPrograms),
  ...present(
    'householdAnnualIncome',
    isBlank(values.householdAnnualIncome)
      ? undefined
      : Number(values.householdAnnualIncome),
  ),
  // An Invalid Date is truthy but serializes to null, which the route refuses.
  ...present(
    'submissionDateTime',
    isValidDate(values.submissionDateTime)
      ? moment(values.submissionDateTime).toISOString()
      : undefined,
  ),
  ...present('preferences', values.preferences),
  // Joi.boolean() does not coerce the radio group's 'yes' / 'no'.
  ...present(
    'requestAccommodation',
    isBlank(values.requestAccommodation)
      ? undefined
      : values.requestAccommodation === 'yes',
  ),
  ...present('accommodationRequested', values.accommodationRequested),
  // Same coercion as the accommodation radio above; the route writes it to
  // prefersUnitAccessible.
  ...present(
    'accessibleUnit',
    isBlank(values.accessibleUnit)
      ? undefined
      : values.accessibleUnit === 'yes',
  ),
  ...present('displacementStatusCodeId', values.displacementStatusCodeId),
});

// The edit and reinstate routes PATCH an existing waitlist record, so absence
// and blankness have to mean different things: a field the configuration
// renders is always sent — null when the agent cleared it — and a field the
// configuration hides is left out so the merge does not touch it. That is the
// opposite of `present()` above, which is right for the create-only convert
// route and wrong here.
//
// `waitlistDateRange` is never emitted: the route Joi.forbidden()s it.
const orNull = (value: mixed) => (isBlank(value) ? null : value);

// Joi.boolean() does not coerce the radio groups' 'yes' / 'no'.
const toBooleanOrNull = (value: mixed) =>
  isBlank(value) ? null : value === 'yes';

const toEditPrimaryApplicant = (
  values: Object,
  {
    showDemographics,
    showMailingAddress,
    showContactInformation,
  }: {
    showDemographics: boolean,
    showMailingAddress: boolean,
    showContactInformation: boolean,
  },
) => ({
  primaryApplicant: {
    firstName: orNull(values.firstName),
    middleName: orNull(values.middleName),
    lastName: orNull(values.lastName),
    suffixId: orNull(values.suffix),
    ...(showContactInformation
      ? {
          phoneNumber: orNull(values.phoneNumber),
          phoneType: orNull(values.phoneType),
          emailAddress: orNull(values.emailAddress),
          emailType: orNull(values.emailType),
        }
      : {}),
    ...(showDemographics
      ? {
          dateOfBirth: values.dateOfBirth
            ? formatDateDB(values.dateOfBirth)
            : null,
          gender: orNull(values.gender),
          // An empty multi-select is "reported no races", not "unanswered".
          multiRace: values.multiRace ?? [],
          ethnicity: orNull(values.ethnicity),
        }
      : {}),
    ...(showMailingAddress
      ? {
          mailingAddress: {
            street: orNull(values.address),
            city: orNull(values.city),
            stateId: orNull(values.state),
            zipCode: orNull(values.zipCode),
          },
        }
      : {}),
  },
});

export const toEditPayload = (
  values: Object,
  fieldVisibility: Object,
  {
    canEditSubmissionDateTime = true,
  }: { canEditSubmissionDateTime?: boolean } = {},
): Object => {
  const {
    showHouseholdIncome,
    showSubmissionStamp,
    showDemographics,
    showMailingAddress,
    showContactInformation,
    showAccommodation,
    showDisplacement,
    showPreferences,
  } = fieldVisibility;

  return {
    adults: Number(values.adults),
    minors: Number(values.minors),
    selectedPrograms: values.selectedPrograms ?? [],
    ...(showHouseholdIncome
      ? {
          householdAnnualIncome: isBlank(values.householdAnnualIncome)
            ? null
            : Number(values.householdAnnualIncome),
        }
      : {}),
    // The one field that is left out rather than nulled when it has no value
    // to send: the route takes a date or nothing at all, and null is refused.
    // It is also left out where the agent may not move it — echoing the value
    // back would revert whatever another agent changed it to in the meantime.
    ...(showSubmissionStamp &&
    canEditSubmissionDateTime &&
    isValidDate(values.submissionDateTime)
      ? {
          submissionDateTime: moment(values.submissionDateTime).toISOString(),
        }
      : {}),
    ...toEditPrimaryApplicant(values, {
      showDemographics,
      showMailingAddress,
      showContactInformation,
    }),
    floorPlanId: orNull(values.floorPlan),
    bedrooms: (values.bedrooms ?? []).map(toBedroom),
    accessibleUnit: toBooleanOrNull(values.accessibleUnit),
    ...(showAccommodation
      ? {
          requestAccommodation: toBooleanOrNull(values.requestAccommodation),
          accommodationRequested: orNull(values.accommodationRequested),
        }
      : {}),
    ...(showDisplacement
      ? { displacementStatusCodeId: orNull(values.displacementStatusCodeId) }
      : {}),
    ...(showPreferences ? { preferences: values.preferences ?? [] } : {}),
  };
};
